import { useState } from "react";
import type { AgentDTO, SubjectTypeDTO } from "../../shared/types";
import { post } from "../lib/api";
import { typePath, type ScopeDetail } from "../lib/scopes";
import { AgentAvatar } from "./AgentAvatar";
import { List, ListRow } from "./List";
import { TextLink } from "./TextLink";
import { Button, ErrorText, inputCls, StatusBadge, timeAgo } from "./ui";

/** The agents working under a scope, each with its own conversation with it, and a way to put another one under it. */
export function ScopeAgents({ scope, type, agents, onChanged }: { scope: ScopeDetail; type: SubjectTypeDTO; agents: AgentDTO[]; onChanged: () => void }) {
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only an agent with access to the type can work under one of its scopes.
  const candidates = agents.filter((a) => !a.archivedAt && type.agentIds.includes(a.id) && !scope.scopes.some((s) => s.agentId === a.id));

  const putUnder = async () => {
    setBusy(true);
    setError(null);
    try {
      await post(`/api/agents/${adding}/scopes`, { subjectId: scope.id });
      setAdding("");
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <List
      title="Agents working under it"
      hint="Each has its own conversation with this scope, and runs one at a time."
      empty="No agent works under it yet."
      footer={
        <>
          {scope.archivedAt ? (
            <p className="text-xs text-muted">Archived: no agent can be put under it.</p>
          ) : type.agentIds.length === 0 ? (
            <p className="text-xs text-muted">
              No agent has access to {type.name} yet. Give one access on <TextLink to={typePath(type.key)}>the type's page</TextLink>.
            </p>
          ) : candidates.length === 0 ? (
            <p className="text-xs text-muted">Every agent with access to {type.name} works under it.</p>
          ) : (
            <div className="flex gap-2">
              <select className={`${inputCls} min-w-0`} value={adding} onChange={(e) => setAdding(e.target.value)} aria-label="Agent to put under this scope">
                <option value="">Put an agent under this scope…</option>
                {candidates.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <Button disabled={busy || !adding} onClick={putUnder}>Add</Button>
            </div>
          )}
          {error && (
            <div className="mt-2">
              <ErrorText error={error} />
            </div>
          )}
        </>
      }
    >
      {scope.scopes.map((s) => (
        <ListRow
          key={s.id}
          leading={<AgentAvatar agent={s.agentId} name={s.agentName} size="sm" />}
          title={<TextLink quiet to={`/admin/agents/${s.agentId}`} className="block truncate text-sm font-medium">{s.agentName}</TextLink>}
          trailing={
            s.conversationId && (
              <TextLink to={`/admin/conversations/${s.conversationId}`}>
                Conversation
              </TextLink>
            )
          }
        >
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted">
            <span>{s.runCount} {s.runCount === 1 ? "run" : "runs"}</span>
            {s.lastRunStatus && (
              <>
                <StatusBadge status={s.lastRunStatus} />
                <span>{timeAgo(s.lastRunAt)}</span>
              </>
            )}
          </div>
        </ListRow>
      ))}
    </List>
  );
}
